import { Button, PasswordInput } from '@mantine/core';
import React from 'react';
import styled from 'styled-components';
import { Colors } from '../patterns/Colors';

interface Props {
    email: string;
    password: string;
    onPasswordChange: (password: string) => void;
    onSubmit: () => void;
    onUseCode: () => void;
    onForgotPassword: () => void;
    signingIn: boolean;
    sendingCode: boolean;
    error?: string | null;
}

export const PasswordLoginForm = ({ email, password, onPasswordChange, onSubmit, onUseCode, onForgotPassword, signingIn, sendingCode, error }: Props) => {

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!password || signingIn || sendingCode) return;
        onSubmit();
    };

    const handleUseCode = (e: React.MouseEvent) => {
        e.preventDefault();
        if (signingIn || sendingCode) return;
        onUseCode();
    };

    const handleForgotPassword = (e: React.MouseEvent) => {
        e.preventDefault();
        if (signingIn || sendingCode) return;
        onForgotPassword();
    };

    return <Form onSubmit={handleSubmit}>
        <Intro>
            Enter the password for <strong>{email}</strong> to sign in.
        </Intro>
        <PasswordInput
            placeholder="Your password"
            value={password}
            onChange={e => onPasswordChange(e.currentTarget.value)}
            autoFocus
            autoComplete="current-password"
            aria-label="Password"
            disabled={signingIn}
            w="100%"
            mb="md"
        />
        <Button
            type="submit"
            loading={signingIn}
            color="primary"
            size="sm"
            fullWidth
        >
            Sign in
        </Button>
        {error && <ErrorText>{error}</ErrorText>}
        <Alternative>
            Prefer a one-time code?{' '}
            <AlternativeLink href="#" onClick={handleUseCode} aria-disabled={sendingCode}>
                {sendingCode ? 'Sending…' : 'Email me a code instead.'}
            </AlternativeLink>
        </Alternative>
        <Alternative>
            <AlternativeLink href="#" onClick={handleForgotPassword} aria-disabled={sendingCode}>
                Forgot your password?
            </AlternativeLink>
        </Alternative>
    </Form>;
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const Intro = styled.p`
    font-size: 0.875rem;
    color: ${Colors.text};
    margin: 0 0 1rem;
`;

const ErrorText = styled.p`
    font-size: 0.8125rem;
    color: #b91c1c;
    margin: 0.75rem 0 0;
    text-align: center;
`;

const Alternative = styled.p`
    font-size: 0.875rem;
    color: ${Colors.text};
    margin: 1rem 0 0;
    text-align: center;
`;

const AlternativeLink = styled.a`
    color: ${Colors.primary};
    text-decoration: none;
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }

    &[aria-disabled="true"] {
        pointer-events: none;
        opacity: 0.6;
    }
`;
